import { apiGet, apiPost, apiPut, siteScoped } from './client';
import type { SiteConfig, Page } from '@/types';

/**
 * Loads the tenant's site config AND merges in the list of pages.
 *
 * The gateway's `GET /site-config` returns the `site_configs.config` JSON blob,
 * which for many tenants does NOT include a `pages` array (pages live in the
 * `site_pages` table). The structured-pages rebuild only happens on the
 * slug-based `GET /site-config/:slug` route used by tenant-sites. The builder
 * needs both, so we fetch `/pages` in parallel and graft them onto the config.
 *
 * If `/pages` fails for any reason we degrade to an empty array — the store's
 * `setSiteConfig` also defaults `pages ?? []` defensively, so the editor still
 * loads instead of white-screening on a missing field.
 */
/**
 * One Brand Manager field, read fresh from the server (not the Studio's cached config). The chat's
 * contact-edit undo checks it right before writing, so a change made in Brand Manager or another
 * tab since is never overwritten (review on #741).
 */
export async function getBrandField(key: 'company_name' | 'tagline' | 'phone' | 'email' | 'address'): Promise<string | null> {
  const config = await apiGet<{ brand?: Record<string, unknown> | null }>('/site-config');
  const v = config?.brand?.[key];
  return typeof v === 'string' ? v : null;
}

export async function getSiteConfig(siteId?: string | null): Promise<SiteConfig> {
  // BOTH requests carry the same pin, or the graft below joins two different sites: `/site-config`
  // answering for the site named here and `/pages` for whatever the store held a tick later. That
  // would not look like an error — it would look like a site whose pages belong to someone else.
  //
  // Omitted (or null) → no pin → the interceptor picks, exactly as before.
  const scope = siteScoped(siteId);
  const [config, pagesResp] = await Promise.all([
    apiGet<SiteConfig>('/site-config', undefined, scope),
    apiGet<{ pages: Page[] }>('/pages', undefined, scope).catch(() => ({ pages: [] as Page[] })),
  ]);
  // Normalize each page so consumers that iterate page.components (Canvas,
  // ComponentSidebar, etc.) don't trip on the /pages endpoint omitting that
  // field — `components: Component[]` is the type's contract but the API
  // surface only carries it when the legacy Proty path is active. Empty array
  // is the safe default for the block-model path D4a is verifying.
  const pages = (pagesResp?.pages ?? []).map((p) => ({
    ...p,
    components: p.components ?? [],
  }));
  // Navigation comes straight from GET /site-config, which reads the canonical
  // `site_navigation` table (site-scoped) and returns FLAT items carrying `id` +
  // `parent_id` — exactly what MenuEditor needs to rebuild dropdowns. Do NOT
  // re-fetch GET /navigation and remap to {label,url,page_id}: that dropped
  // id/parent_id (and nested children), so nested menus collapsed to a flat
  // top-level list on every reload (the "nesting doesn't persist" bug).
  return { ...config, pages };
}

/*
 * There is deliberately no whole-config writer here any more.
 *
 * `putSiteConfig(config)` sent the entire object, and every caller built that object from a
 * config it had just read — so it handed back the values `GET /site-config` computes rather than
 * stores, with every media URL already rebound to whichever API host served the read. The gateway
 * now strips the computed keys, which left such a write doing nothing for those and re-authoring
 * the rest. Each surface writes the subtree it owns instead: `updateSiteContent`,
 * `setHeroMedia`, `setSectionLooks`, `setTemplateOverrides` here, plus
 * `saveStyle` (PUT /style), `saveNavigation` (PUT /navigation) and the pages API.
 */

/**
 * Merge keys into `config.content` — the workspace store for agent profiles, offices, blog
 * posts, community profiles and page copy.
 *
 * Targeted partial write. This replaces `updateSiteConfig(updater)`, which read the whole config,
 * applied a change and PUT the entire object back. Six workspaces used it, each to change ONE key
 * under `content`, and each sent the other five back exactly as they had been at read time — so
 * two of them open at once (or one left open while chat edited the site) meant whoever saved last
 * silently reverted the other. The gateway deep-merges, and replaces arrays wholesale, so sending
 * only the changed key both removes the race and still expresses a deletion or a reorder.
 *
 * Returns the full merged config the gateway writes back. Note it is the STORED config: it does
 * not carry the keys the authenticated GET resolves (brand, style, navigation, pages), so it must
 * not be written into a cache whose queryFn is `getSiteConfig` — use `patchCachedSiteContent`.
 */
export async function updateSiteContent(
  patch: Record<string, unknown>,
  expectedFingerprints?: Record<string, string>,
): Promise<SiteConfig> {
  return apiPut<SiteConfig>('/site-config', { content: patch, ...(expectedFingerprints ? { expectedFingerprints } : {}) });
}

/**
 * Did this save lose a race? The gateway answers 409 with a `CONFIG_CONFLICT:` message when the
 * key being written changed since the client read it; the axios interceptor puts the server's
 * sentence on `error.message`. Matching the prefix rather than the status keeps this true if the
 * request is ever proxied or wrapped.
 */
export function isConfigConflict(error: unknown): boolean {
  return error instanceof Error && error.message.includes('CONFIG_CONFLICT');
}

/**
 * The `expectedFingerprints` argument for one path, from the config a workspace is holding.
 *
 * Returns undefined when the config (or its token) is missing, which makes the save behave as it
 * did before — unguarded. That is the right failure direction: a gateway that predates the
 * feature, or a cache warmed from a response without tokens, must not make saving impossible.
 */
export function expectedFor(
  config: Pick<SiteConfig, 'fingerprints'> | undefined,
  path: string,
): Record<string, string> | undefined {
  const token = config?.fingerprints?.[path];
  return token ? { [path]: token } : undefined;
}

/**
 * Promote the site shell's draft to published.
 *
 * This used to be dead: it POSTed to `/site-config/publish`, which the gateway did not implement,
 * so the retired builder's Publish button could never have worked. The route exists now
 * (Phase 2, step 1), and the shell has a draft for it to promote.
 *
 * ## "Nothing to publish" is not a failure
 *
 * The gateway answers 400 when `draft_config IS NULL` and 404 when the site has no config row at
 * all. Both mean "there was nothing here to promote", which is the ordinary state of a site whose
 * shell has not been touched — and publishing PAGES must not be reported as failed because the
 * shell had no changes. So those two are `{ published: false }`, not a throw.
 *
 * Anything else does throw: a 409 means the draft moved while we were promoting it, and a 500
 * means the promotion did not happen. Those the agent needs to know about, because their site is
 * not showing what they just published.
 */
export async function publishSiteConfig(): Promise<{ published: boolean }> {
  try {
    await apiPost('/site-config/publish');
    return { published: true };
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 400 || status === 404) return { published: false };
    throw err;
  }
}

/** One site's draft/published relationship, as `/site-parity` reports it. */
export interface SiteParityRow {
  site_id: string;
  version: number;
  has_config: boolean;
  has_draft: boolean;
  in_parity: boolean;
  /**
   * The OTHER shell stores. Optional because a gateway that predates them answers without.
   *
   * The shell is four homes, and this row described one. After the flip a colour change or a menu
   * change lands in its own draft and never touches `site_configs`, so a Publish button driven by
   * `in_parity` alone would stay greyed out over work the agent had just done.
   */
  style_diverged?: boolean;
  nav_diverged?: boolean;
  /**
   * The BRAND — the fourth store, and the one that reaches further than this site. A logo drafted
   * from the builder touches none of the three above, so without this the Publish button greys
   * out over work the agent just did.
   */
  brand_diverged?: boolean;
  /** The PROFILE — name, headshot, bio, licence. Tenant-level, so the same for every site. */
  profile_diverged?: boolean;
}

export interface SiteParity {
  column_missing: boolean;
  sites: SiteParityRow[];
  summary: {
    total: number; with_draft: number; in_parity: number;
    missing_draft: number; no_config: number; diverged: number;
  };
  ready_to_flip: boolean;
  /** Does saving still put a shell change in front of visitors? Absent on a gateway that predates it. */
  writes_reach_visitors?: boolean;
}

/**
 * Does saving a shell change still put it in front of visitors?
 *
 * The product has one answer at a time and the gateway owns it (`WRITES_PUBLISH_DIRECTLY` in
 * site-config-write.ts). Asking rather than assuming is what lets a surface say something true on
 * both sides of the flip without a second deploy — the Blog workspace's "Toggle this on when the
 * post is ready for the live site" is accurate today and a lie afterwards.
 *
 * Defaults to TRUE, including when the read fails or the gateway is too old to answer. That is
 * today's behaviour and today's copy: the wrong direction to fail in would be telling an agent
 * their work needs publishing on a deploy where saving already published it, which sends them
 * looking for a button to fix a problem they do not have.
 */
export async function savingReachesVisitors(): Promise<boolean> {
  try {
    const parity = await apiGet<SiteParity>('/site-parity');
    return parity?.writes_reach_visitors !== false;
  } catch {
    return true;
  }
}

/**
 * Does this site's SHELL have changes that have not been published?
 *
 * Reuses the parity gate rather than adding a field to `GET /site-config`, because §1.1: that
 * response is read, held and written back, and anything derived added to it risks being persisted
 * into authored storage as though someone typed it.
 *
 * `in_parity` means the draft equals what is published. So a site with a draft that is NOT in
 * parity has shell edits a visitor cannot see yet — which is exactly "there is something to
 * publish".
 *
 * Today this is always false: the seam writes both columns to the same value, so no shell can be
 * out of parity. That is deliberate. It means wiring it into the Publish button changes nothing
 * now and is already correct when the flip makes drafts diverge — the button starts offering to
 * publish shell changes on the day they start needing it, with no second change to make.
 */
/**
 * The other sites a Publish would also change, because they share this site's brand.
 *
 * The brand is TENANT-level while Publish is site-level, so promoting a brand draft from the
 * builder puts the new logo, name and contact details on every site using that brand. For a solo
 * agent that is invisible and correct. For an agency it is a surprise, and the answer to a
 * surprise is to name it rather than to hide the reach or refuse the publish.
 *
 * Empty when nothing else shares the brand, when the brand has no pending changes, or when we
 * cannot tell — the last of those deliberately, because a failed read must not manufacture a
 * warning naming sites it did not actually check.
 */
export async function sitesSharingPendingBrand(
  siteId: string | null,
  sites: Array<{ id: string; name: string; brand_id?: string | null }> | undefined,
): Promise<string[]> {
  if (!siteId || !sites?.length) return [];
  try {
    const parity = await apiGet<SiteParity>('/site-parity');
    const row = parity?.sites?.find((s) => s.site_id === siteId);
    if (row?.brand_diverged !== true) return [];
    const brandId = sites.find((s) => s.id === siteId)?.brand_id ?? null;
    if (!brandId) return [];
    return sites.filter((s) => s.id !== siteId && s.brand_id === brandId).map((s) => s.name);
  } catch {
    return [];
  }
}

export async function shellHasUnpublishedChanges(siteId: string | null): Promise<boolean> {
  if (!siteId) return false;
  try {
    const parity = await apiGet<SiteParity>('/site-parity');
    const row = parity?.sites?.find((s) => s.site_id === siteId);
    if (!row) return false;
    // ANY of the five, not just the config. Each is a separate store with its own draft, and
    // Publish promotes all of them in one transaction — so anything that can be promoted has to
    // be able to turn the button on. A gateway too old to report the others answers undefined,
    // which leaves the behaviour exactly as it was.
    const configDiverged = row.has_draft && !row.in_parity;
    return configDiverged
      || row.style_diverged === true
      || row.nav_diverged === true
      || row.brand_diverged === true
      || row.profile_diverged === true;
  } catch {
    // A failed read is not an answer of "nothing to publish" — but it is also not a reason to
    // claim there IS something. False keeps the button exactly as it behaves today; the page
    // signal still enables it, so this can never take away an agent's ability to publish.
    return false;
  }
}

/**
 * Persist the hero media for the home look's hero. Targeted partial write — the gateway merges
 * it into `site_configs.config.hero_media`, so unrelated config (pages, brand, nav) is untouched.
 * The SAME write the Hero Media panel performs; the tenant home render and the Studio preview
 * both prefer this over `style.hero_image_url` when painting the hero background.
 */
export async function setHeroMedia(heroMedia: NonNullable<SiteConfig['hero_media']>): Promise<void> {
  await apiPut('/site-config', { hero_media: heroMedia });
}

/**
 * Persist the per-section "Pick a Look" selection (home/search/listing/card/dark). Targeted
 * partial write — the gateway merges it into `site_configs.config.section_looks`, so unrelated
 * config (pages, brand, nav) is untouched. Same write the Pick-a-Look page uses; scoped to the
 * active site via the X-Site-Id header the client interceptor attaches.
 */
export async function setSectionLooks(looks: Record<string, string>): Promise<void> {
  await apiPut('/site-config', { section_looks: looks });
}

/**
 * Persist a style-token override (Template Override Spec, docs/TEMPLATE_OVERRIDE_SPEC.md §3/§5/
 * §6.4). Targeted partial write — the gateway merges it into `site_configs.config.
 * template_overrides`, so unrelated config (pages, brand, section_looks) is untouched. Wins over
 * brand style, the active look's own theme, AND the site-wide dark toggle in both renderers.
 */
export async function setTemplateOverrides(overrides: Record<string, string>): Promise<void> {
  await apiPut('/site-config', { template_overrides: overrides });
}

/**
 * Just the override map, for a caller that needs to know what is in force before rewriting it.
 *
 * Deliberately NOT `getSiteConfig()`: that also fetches `/pages` to graft on, and a caller asking
 * "is anything overriding the brand?" has no use for the page list. Degrades to `{}` — a failed
 * read must not be mistaken for "nothing is overridden", so callers that WRITE off the back of it
 * should treat a throw as a failure rather than calling this and hoping.
 */
export async function getTemplateOverrides(): Promise<Record<string, string>> {
  const config = await apiGet<{ template_overrides?: Record<string, string> }>('/site-config');
  return config?.template_overrides ?? {};
}

/**
 * Fetch a "Pick a Look" section preset's blocks (+ theme) — the SAME endpoint the live tenant
 * render uses (apps/tenant-sites fetchSectionLook). Lets the Studio preview apply a non-classic
 * HOME look exactly like the live site, so the editor preview matches what visitors see. Public,
 * no-auth endpoint; degrades to null blocks on any failure (→ Studio falls back to the site's own
 * blocks, its prior behavior).
 */
export interface SectionLookPreset {
  blocks: unknown[] | null;
  theme: Record<string, unknown> | null;
}
export async function fetchSectionLook(section: string, variantKey: string): Promise<SectionLookPreset> {
  if (!section || !variantKey) return { blocks: null, theme: null };
  try {
    const res = await apiGet<SectionLookPreset>(
      `/section-look?section=${encodeURIComponent(section)}&variant_key=${encodeURIComponent(variantKey)}`,
    );
    return {
      blocks: Array.isArray(res?.blocks) ? res.blocks : null,
      theme: res?.theme && typeof res.theme === 'object' ? res.theme : null,
    };
  } catch {
    return { blocks: null, theme: null };
  }
}

/** Remove the hero media override, so the hero falls back to the brand's photo / the look's own.
 *  Undo uses it to put a hero back the way it was when there was no override before. */
export async function clearHeroMedia(): Promise<void> {
  await apiPut('/site-config', { hero_media: null });
}

/** One thing Limited Publish would put live (services/api-gateway/src/lib/limited-publish.ts). */
export interface LimitedChange {
  area: 'content' | 'business_info';
  key: string;
  label: string;
}

/** What "Publish my changes" would publish on the active site: content and business details only. */
export async function getLimitedPublishChanges(): Promise<LimitedChange[]> {
  const res = await apiGet<{ changes: LimitedChange[] }>('/site-config/publish-limited');
  return res.changes ?? [];
}

/**
 * Limited Publish: puts live only agents, offices, teams, blog, communities, profile and business
 * details. Design work in the draft stays unpublished.
 */
export async function publishLimited(): Promise<{ published: LimitedChange[] }> {
  return apiPost<{ published: LimitedChange[] }>('/site-config/publish-limited', {});
}
